import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Sprout,
  TrendingUp,
  Shield,
  Umbrella,
  Satellite,
  ArrowRight,
  Zap,
  Database,
  RefreshCw,
  ChevronRight,
  Globe,
  Building2,
  Users,
  Target,
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import Button from '@/components/ui/Button';
import { Language } from '@/types';

/* ═══════════════════════════════════════════════════════════════════════════
   VEXL Agricultural Intelligence — Ecosystem Landing Page
   "The Intelligence Layer for Every Agricultural Decision in MENA & Africa"
   ═══════════════════════════════════════════════════════════════════════════ */

interface AgriIntelligencePageProps {
  lang: Language;
}

// ─── Product Data ────────────────────────────────────────────────────────────

interface AgriProduct {
  id: string;
  name: string;
  tagline: string;
  desc: string;
  icon: React.ReactNode;
  gradient: string;
  borderColor: string;
  glowColor: string;
  status: string;
  audience: string;
  metric: string;
  path: string;
}

const getProducts = (lang: Language): AgriProduct[] => {
  const isAr = lang === 'ar';
  return [
    {
      id: 'droughtwatch',
      name: 'DroughtWatch AI',
      tagline: isAr ? 'محرك التنبؤ بالجفاف' : 'Drought Prediction Engine',
      desc: isAr
        ? 'محرك ذكاء اصطناعي مكاني يتنبأ بالجفاف الزراعي قبل حدوثه بعدة أشهر بالاعتماد على 25 سنة من بيانات NDVI ونماذج Bi-LSTM مثبتة دولياً من UNEP.'
        : 'Deep learning engine predicting agricultural drought months in advance using 25 years of multi-sensor NDVI and Bi-LSTM neural networks, validated internationally by UNEP.',
      icon: <Satellite size={28} />,
      gradient: 'from-[#0E93A3] to-[#075C68]',
      borderColor: 'border-[#262C31]',
      glowColor: 'shadow-[#0E93A3]/5',
      status: isAr ? 'جاهز للإنتاج' : 'Production',
      audience: isAr ? 'الحكومات والمؤسسات الدولية' : 'Institutions & Govs',
      metric: isAr ? 'ترخيص مؤسسي' : 'Enterprise License',
      path: '/droughtwatch',
    },
    {
      id: 'agroscore',
      name: 'AgroScore',
      tagline: isAr ? 'تقييم الجدارة الائتمانية الزراعية' : 'Agricultural Credit Scoring API',
      desc: isAr
        ? 'واجهة برمجية للبنوك ومؤسسات التمويل الأصغر تحول إحداثيات الحقل ونوع المحصول إلى مؤشر مخاطر ائتمانية دقيق (300–850) مع تدقيق زمني للأرض.'
        : 'Enterprise API for financial institutions translating farm coordinates and crop types into reliable credit risk scores (300–850) based on longitudinal vegetation health.',
      icon: <BarChart3 size={28} />,
      gradient: 'from-[#22C55E] to-[#15803D]',
      borderColor: 'border-[#262C31]',
      glowColor: 'shadow-[#22C55E]/5',
      status: isAr ? 'متاح تجارياً' : 'Live API',
      audience: isAr ? 'البنوك والتمويل الأصغر' : 'Banks & MFIs',
      metric: isAr ? 'حسب الاستعلام' : 'Per-Query API',
      path: '/agri-intelligence/agroscore',
    },
    {
      id: 'croplens',
      name: 'CropLens',
      tagline: isAr ? 'التأمين الزراعي المعياري والتحقق الفضائي' : 'Parametric Crop Insurance & Audit',
      desc: isAr
        ? 'منظومة تسوية مطالبات تأمينية رقمية عبر الأقمار الصناعية — تحقق فوري من تضرر المحاصيل بدون زيارات ميدانية مكلفة مع تقارير تدقيق معتمدة.'
        : 'Remote crop loss verification platform for insurers — instant satellite verification of crop failure triggers and certified geospatial adjuster audits.',
      icon: <Umbrella size={28} />,
      gradient: 'from-[#4E9BB8] to-[#1B4D68]',
      borderColor: 'border-[#262C31]',
      glowColor: 'shadow-[#4E9BB8]/5',
      status: isAr ? 'جاهز للإنتاج' : 'Production',
      audience: isAr ? 'شركات التأمين وإعادة التأمين' : 'Insurers & Reinsurers',
      metric: isAr ? 'اشتراك مؤسسي' : 'Platform License',
      path: '/croplens',
    },
    {
      id: 'cropiq',
      name: 'CropIQ',
      tagline: isAr ? 'لوحة تحكم ذكاء المزارع والإنتاجية' : 'Farm Intelligence Dashboard',
      desc: isAr
        ? 'لوحة تحكم للمشاريع الزراعية الكبرى — رصد دقيق لصحة المحاصيل عبر Sentinel-2، كشف الإجهاد المائي والمؤشرات الحرارية، وتوجيه عمليات الري.'
        : 'High-resolution farm management platform — Sentinel-2 multi-spectral crop vigor tracking, moisture stress anomaly detection, and irrigation guidance.',
      icon: <Sprout size={28} />,
      gradient: 'from-[#22C55E] to-[#0E93A3]',
      borderColor: 'border-[#262C31]',
      glowColor: 'shadow-[#22C55E]/5',
      status: isAr ? 'متاح للاختبار' : 'Pilot Available',
      audience: isAr ? 'الشركات الزراعية الكبرى' : 'Agri-Corporates',
      metric: isAr ? 'حسب المساحة' : 'Per Hectare / Month',
      path: '/agri-intelligence/cropiq',
    },
  ];
};

// ─── Market Stats ────────────────────────────────────────────────────────────

const getStats = (lang: Language) => {
  const isAr = lang === 'ar';
  return [
    { value: '$77B', label: isAr ? 'حجم السوق بحلول 2036' : 'Market Size by 2036', icon: <Globe size={20} /> },
    { value: '26.3%', label: isAr ? 'معدل النمو السنوي' : 'Annual Growth Rate', icon: <TrendingUp size={20} /> },
    { value: '25', label: isAr ? 'سنة من بيانات NDVI' : 'Years of NDVI Data', icon: <Satellite size={20} /> },
    { value: '80%', label: isAr ? 'مزارعين بدون تأمين' : 'Farmers Uninsured', icon: <Users size={20} /> },
  ];
};

// ─── Flywheel Steps ──────────────────────────────────────────────────────────

const getFlywheelSteps = (lang: Language) => {
  const isAr = lang === 'ar';
  return [
    { label: isAr ? 'DroughtWatch يتنبأ بالجفاف' : 'DroughtWatch predicts drought', icon: <Satellite size={18} /> },
    { label: isAr ? 'AgroScore يسكور القرض' : 'AgroScore scores the loan', icon: <BarChart3 size={18} /> },
    { label: isAr ? 'البنك يُرجع نتيجة القرض' : 'Bank returns loan outcome', icon: <Building2 size={18} /> },
    { label: isAr ? 'بيانات جديدة تغذي الموديل' : 'New data feeds the model', icon: <Database size={18} /> },
    { label: isAr ? 'الموديل يبقى أدق' : 'Model becomes more accurate', icon: <Target size={18} /> },
    { label: isAr ? 'كل المنتجات تتحسن' : 'All products improve', icon: <RefreshCw size={18} /> },
  ];
};

// ─── Product Card Component ──────────────────────────────────────────────────

const ProductCard: React.FC<{ product: AgriProduct; index: number; lang: Language }> = ({ product, index, lang }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const isAr = lang === 'ar';

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(product.path)}
      className="group relative rounded-xl overflow-hidden cursor-pointer bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all duration-300 hover:shadow-xl hover:shadow-black/60"
    >
      {/* Top Gradient Accent */}
      <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${product.gradient} opacity-80 group-hover:opacity-100 transition-opacity`} />

      {/* Content */}
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg bg-gradient-to-br ${product.gradient} text-white shadow-lg`}>
              {product.icon}
            </div>
            <div>
              <h3 className="text-xl font-semibold tracking-tight text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">
                {product.name}
              </h3>
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#9AA5AC] mt-0.5">
                {product.tagline}
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border border-[#262C31] bg-[#05070A] text-[#9AA5AC]">
            {product.status}
          </span>
        </div>

        {/* Description */}
        <p className={`text-sm text-[#9AA5AC] leading-relaxed mb-6 ${isAr ? 'text-right' : ''}`}>
          {product.desc}
        </p>

        {/* Footer Meta */}
        <div className="flex items-center justify-between pt-4 border-t border-[#262C31]">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#66707A]">
                {isAr ? 'القطاع المستهدف' : 'Target Sector'}
              </p>
              <p className="text-xs font-medium text-[#EDF0F1] mt-0.5">
                {product.audience}
              </p>
            </div>
            <div className="w-px h-8 bg-[#262C31]" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#66707A]">
                {isAr ? 'نموذج الإتاحة' : 'Availability'}
              </p>
              <p className="text-xs font-medium text-[#0E93A3] font-mono mt-0.5">
                {product.metric}
              </p>
            </div>
          </div>

          <motion.div
            animate={{ x: isHovered ? 4 : 0, opacity: isHovered ? 1 : 0.6 }}
            transition={{ duration: 0.2 }}
          >
            <ArrowRight size={18} className="text-[#0E93A3]" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Main Page Component ─────────────────────────────────────────────────────

export const AgriIntelligencePage: React.FC<AgriIntelligencePageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const navigate = useNavigate();
  const products = getProducts(lang);
  const stats = getStats(lang);
  const flywheelSteps = getFlywheelSteps(lang);

  const t = {
    badge: isAr ? 'ذكاء زراعي' : 'AGRICULTURAL INTELLIGENCE',
    title: isAr
      ? 'الطبقة الذكية لكل\nقرار زراعي في MENA وإفريقيا'
      : 'The Intelligence Layer for\nEvery Agricultural Decision',
    subtitle: isAr
      ? 'منظومة استخبارات زراعية متكاملة. محرك فضائي موحد. نماذج تنبؤية مدربة على 25 عاماً من الأقمار الصناعية ومثبتة دولياً من UNEP.'
      : 'Integrated agricultural intelligence suite. One unified GeoAI engine. Predictive models trained on 25 years of multi-satellite data and validated by UNEP.',
    productsTitle: isAr ? 'المنظومة الزراعية المتكاملة' : 'The Agricultural Suite',
    productsSubtitle: isAr
      ? 'أدوات ومنصات تخدم البنوك، شركات التأمين، والجهات الحكومية — قائمة على التحليل الطيفي والذكاء المكاني الفوري.'
      : 'Mission-critical tools for banks, insurers, and government entities — powered by multi-spectral remote sensing.',
    engineTitle: isAr ? 'المحرك الأساسي: DroughtWatch AI' : 'The Engine: DroughtWatch AI',
    engineDesc: isAr
      ? 'محرك ذكاء اصطناعي متخصص في التنبؤ بالجفاف وتقييم صحة المحاصيل. مبني على 25 سنة من بيانات NDVI عبر الأقمار الصناعية (Sentinel-2, Landsat, MODIS) ومثبت ميدانياً في الفيوم بدقة NDVI = 0.16.'
      : 'AI engine specialized in drought prediction and crop health assessment. Built on 25 years of satellite NDVI data (Sentinel-2, Landsat, MODIS) and field-validated in Fayoum with NDVI = 0.16 accuracy.',
    flywheelTitle: isAr ? 'دورة التغذية الراجعة للبيانات' : 'The Data Flywheel',
    flywheelDesc: isAr
      ? 'منظومة تعلم مكاني مستمرة — كل تدقيق ميداني وعملية تأمين تغذي نماذجنا الفضائية لتزيد دقة التنبؤ عبر المنطقة.'
      : 'A continuous geospatial learning loop — every field audit and insurance verification feeds the models to compound regional accuracy.',
    marketTitle: isAr ? 'أرقام السوق والفرصة الاقتصادية' : 'Market Opportunity',
    ctaPrimary: isAr ? 'تواصل معنا' : 'Get in Touch',
    ctaSecondary: isAr ? 'اقرأ البحث' : 'Read the Research',
    validation: isAr ? 'مثبت ومعتمد من' : 'Validated by',
  };

  return (
    <PageTransition>
      <SEO
        title={isAr ? 'VEXL — ذكاء زراعي' : 'VEXL — Agricultural Intelligence'}
        description={isAr
          ? 'الطبقة الذكية لكل قرار زراعي في MENA وإفريقيا — من البنك للمزارع للتاجر للحكومة.'
          : 'The intelligence layer for every agricultural decision in MENA & Africa — from bank to farm to trader to government.'}
      />

      <div className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden ${isAr ? 'rtl' : 'ltr'}`}>
        <RasterGridBackdrop type="type-a" />

        {/* ══════ HERO SECTION (Editorial Split) ══════ */}
        <section className="relative pt-32 pb-16 border-b border-[#262C31] z-10">
          <div className="w-full max-w-[1400px] mx-auto px-6">
            <div
              className={`
                w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
                ${isAr ? 'text-right' : 'text-left'}
              `}
            >
              {/* Left Column (Headline) */}
              <div className="lg:col-span-7">
                <h1
                  className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {t.title}
                </h1>
              </div>

              {/* Right Column (Sub-Description & CTAs) */}
              <div className="lg:col-span-5 space-y-6">
                <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                  {t.subtitle}
                </p>
                <div className="flex items-center gap-3 pt-2 flex-wrap">
                  <button
                    onClick={() => navigate('/contact')}
                    className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs tracking-wide shadow-[0_0_15px_rgba(14,147,163,0.3)] transition-all duration-200"
                  >
                    <span>{t.ctaPrimary}</span>
                    <ArrowRight size={13} className={isAr ? "rotate-180 group-hover:-translate-x-0.5 transition-transform" : "group-hover:translate-x-0.5 transition-transform"} />
                  </button>
                  <button
                    onClick={() => navigate('/research')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3] text-[#EDF0F1] font-medium text-xs tracking-wide transition-all duration-200"
                  >
                    <span>{t.ctaSecondary}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* UNEP Validation Telemetry Bar */}
            <div className="pt-12 mt-12 border-t border-[#262C31]/60 flex items-center justify-start gap-4 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#66707A]">
                {t.validation}
              </span>
              <div className="h-3 w-px bg-[#262C31]" />
              <span className="text-xs font-mono text-[#EDF0F1] tracking-wide">
                UNEP — 3rd Place International
              </span>
              <div className="h-3 w-px bg-[#262C31]" />
              <span className="text-xs font-mono text-[#0E93A3] tracking-wide">
                NARSS Validated
              </span>
            </div>
          </div>
        </section>

        {/* ══════ MARKET STATS ══════ */}
        <section className="py-16 border-y border-[#262C31] bg-[#0B0F12]/50 relative z-10">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-[#EDF0F1] tracking-tight">
                {t.marketTitle}
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="text-center p-6 rounded-xl bg-[#12161A] border border-[#262C31]"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#05070A] border border-[#262C31] text-[#0E93A3] mb-4">
                    {stat.icon}
                  </div>
                  <p className="text-3xl md:text-4xl font-mono font-semibold text-[#EDF0F1] tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-xs font-mono uppercase tracking-wider text-[#9AA5AC] mt-2">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════ ENGINE SECTION ══════ */}
        <section className="py-24 relative z-10 border-b border-[#262C31]">
          <div className="relative max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center mb-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#12161A] border border-[#262C31] text-[#0E93A3] mb-6 font-mono text-[10px] uppercase tracking-[0.2em]">
                <Zap size={12} />
                <span>CORE ENGINE</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#EDF0F1] tracking-tight mb-6">
                {t.engineTitle}
              </h2>
              <p className="text-base text-[#9AA5AC] max-w-3xl mx-auto leading-relaxed">
                {t.engineDesc}
              </p>
            </motion.div>

            {/* Engine Visual — Data Sources */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              {[
                { name: 'Sentinel-2', detail: '10m • 5-day revisit', color: 'text-[#0E93A3]' },
                { name: 'Landsat 8/9', detail: '30m • 16-day revisit', color: 'text-[#4E9BB8]' },
                { name: 'MODIS', detail: '250m • Daily', color: 'text-[#7F77DD]' },
              ].map((src, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl bg-[#12161A] border border-[#262C31] text-center"
                >
                  <Satellite size={24} className={`${src.color} mx-auto mb-3`} />
                  <p className="text-sm font-semibold text-[#EDF0F1]">{src.name}</p>
                  <p className="text-xs text-[#9AA5AC] mt-1 font-mono">{src.detail}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ══════ PRODUCTS SECTION ══════ */}
        <section className="py-24 relative z-10">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-semibold text-[#EDF0F1] tracking-tight mb-4">
                {t.productsTitle}
              </h2>
              <p className="text-base text-[#9AA5AC] max-w-2xl mx-auto leading-relaxed">
                {t.productsSubtitle}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {products.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} lang={lang} />
              ))}
            </div>
          </div>
        </section>

        {/* ══════ FLYWHEEL SECTION ══════ */}
        <section className="py-24 border-t border-[#262C31] relative z-10">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#12161A] border border-[#262C31] text-[#D48207] mb-6 font-mono text-[10px] uppercase tracking-[0.2em]">
                <RefreshCw size={12} />
                <span>DATA FLYWHEEL</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#EDF0F1] tracking-tight mb-4">
                {t.flywheelTitle}
              </h2>
              <p className="text-base text-[#9AA5AC] max-w-2xl mx-auto leading-relaxed">
                {t.flywheelDesc}
              </p>
            </motion.div>

            {/* Flywheel Steps — Vertical Chain */}
            <div className="relative max-w-lg mx-auto">
              {/* Connecting Line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-[#0E93A3]/30 via-[#22C55E]/30 to-[#D48207]/30" />

              <div className="space-y-6">
                {flywheelSteps.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex items-center gap-5 group"
                  >
                    <div className="relative z-10 w-12 h-12 rounded-full bg-[#12161A] border border-[#262C31] flex items-center justify-center text-[#9AA5AC] group-hover:border-[#0E93A3] group-hover:text-[#0E93A3] transition-all duration-300 shrink-0">
                      {step.icon}
                    </div>
                    <div className="flex-1 p-4 rounded-xl bg-[#12161A] border border-[#262C31] group-hover:border-[#0E93A3]/40 transition-all duration-300">
                      <p className="text-sm font-medium text-[#EDF0F1]">
                        {step.label}
                      </p>
                    </div>
                    {i < flywheelSteps.length - 1 && (
                      <ChevronRight size={14} className="text-[#66707A] rotate-90 absolute left-[18px] translate-y-[30px]" />
                    )}
                  </motion.div>
                ))}

                {/* Loop Back Indicator */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="flex items-center gap-5"
                >
                  <div className="relative z-10 w-12 h-12 rounded-full bg-[#0E93A3] flex items-center justify-center text-white shadow-lg shadow-[#0E93A3]/20 shrink-0">
                    <RefreshCw size={18} />
                  </div>
                  <div className="flex-1 p-4 rounded-xl bg-[#12161A] border border-[#0E93A3]/40">
                    <p className="text-sm font-mono font-semibold text-[#0E93A3]">
                      {isAr ? '↻ تكرار مستمر — كل تقييم يغذي DroughtWatch مجدداً' : '↻ Continuous Loop — Every outcome retrains DroughtWatch'}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════ CTA SECTION ══════ */}
        <section className="py-24 relative overflow-hidden border-t border-[#262C31] z-10">
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl md:text-4xl font-semibold text-[#EDF0F1] tracking-tight mb-6">
                {isAr
                  ? 'جاهز للاستفادة من البنية التحتية للذكاء الزراعي؟'
                  : 'Ready to Deploy Agricultural GeoAI?'}
              </h2>
              <p className="text-base text-[#9AA5AC] mb-10 leading-relaxed font-normal">
                {isAr
                  ? 'سواء كنت بنكاً زراعياً، شركة تأمين، أو جهة حكومية — نوفر لك تقييماً مكانياً دقيقاً مبنياً على 25 سنة من السجلات الطيفية.'
                  : 'Whether you are an agricultural bank, insurer, or institutional agency — access certified geospatial intelligence trained on 25 years of spectral data.'}
              </p>

              <div className="flex items-center justify-center gap-4 flex-wrap">
                <button
                  onClick={() => navigate('/contact')}
                  className="group inline-flex items-center gap-2 px-8 py-3.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white font-mono font-semibold text-xs tracking-wider uppercase transition-all duration-200"
                >
                  {isAr ? 'تواصل مع فريقنا' : 'Talk to Our Team'}
                  <ArrowRight size={14} className={isAr ? "rotate-180 group-hover:-translate-x-1 transition-transform" : "group-hover:translate-x-1 transition-transform"} />
                </button>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default AgriIntelligencePage;
